import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import './WholesaleApplicationsPage.css';

function StatusBadge({ status }) {
  const cls = status === 'approved' ? 'whapp-badge whapp-badge-approved'
            : status === 'denied'   ? 'whapp-badge whapp-badge-denied'
            : 'whapp-badge whapp-badge-pending';
  return <span className={cls}>{status}</span>;
}

export default function WholesaleApplicationsPage() {
  const navigate = useNavigate();
  const { appId } = useParams();

  const [applications, setApplications] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [deciding,     setDeciding]     = useState(null); // application id currently being decided
  const [error,        setError]        = useState(null);
  const [expanded,     setExpanded]     = useState(appId || null);

  function reload() {
    setLoading(true);
    axios.get('/api/wholesale/applications')
      .then(r => { setApplications(r.data || []); setLoading(false); })
      .catch(err => {
        const status = err.response?.status;
        setError(status === 401
          ? 'Failed to load applications — you may have been signed out. Try refreshing the page.'
          : `Failed to load applications${status ? ` (${status})` : ''}`);
        setLoading(false);
      });
  }

  useEffect(() => { reload(); }, []);

  function decide(id, decision) {
    setDeciding(id);
    axios.post(`/api/wholesale/applications/${id}/decide`, { decision })
      .then(r => {
        setApplications(prev => prev.map(a => a.id === id ? r.data : a));
        setDeciding(null);
      })
      .catch(err => {
        setError(err.response?.data?.error || `Failed to ${decision === 'approved' ? 'approve' : 'deny'} application`);
        setDeciding(null);
      });
  }

  const pending = applications.filter(a => a.status === 'pending');
  const decided = applications.filter(a => a.status !== 'pending');

  function Row({ a }) {
    const isOpen = expanded === a.id;
    return (
      <React.Fragment>
        <tr className="whapp-row" onClick={() => { setExpanded(isOpen ? null : a.id); navigate(`/wholesale/applications/${a.id}`, { replace: true }); }}>
          <td className="whapp-owner">{a.owner_name}</td>
          <td>{a.email}</td>
          <td>{a.region}{a.is_eu ? <span className="whapp-eu-tag">EU</span> : null}</td>
          <td className="whapp-muted-sm">{new Date(a.submitted_at).toLocaleString()}</td>
          <td><StatusBadge status={a.status} /></td>
        </tr>
        {isOpen && (
          <tr className="whapp-expand-row">
            <td colSpan={5}>
              <div className="whapp-detail">
                <div className="whapp-detail-grid">
                  <div><span className="whapp-detail-label">Business / TIN</span>{a.business_tin}</div>
                  <div><span className="whapp-detail-label">Address</span>{a.address}</div>
                  {a.status !== 'pending' && (
                    <>
                      <div><span className="whapp-detail-label">Decision</span>{a.decision} by {a.decided_by} at {new Date(a.decided_at).toLocaleString()}</div>
                      {a.customer_admin_link && (
                        <div>
                          <span className="whapp-detail-label">Shopify customer</span>
                          <a href={a.customer_admin_link} target="_blank" rel="noreferrer">{a.shopify_customer_id}</a>
                        </div>
                      )}
                    </>
                  )}
                </div>
                {a.status === 'pending' && (
                  <div className="whapp-actions">
                    <button
                      className="whapp-btn-approve"
                      disabled={deciding === a.id}
                      onClick={e => { e.stopPropagation(); decide(a.id, 'approved'); }}
                    >
                      Approve
                    </button>
                    <button
                      className="whapp-btn-deny"
                      disabled={deciding === a.id}
                      onClick={e => { e.stopPropagation(); decide(a.id, 'denied'); }}
                    >
                      Deny
                    </button>
                  </div>
                )}
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  }

  return (
    <div className="whapp-page">
      <div className="whapp-header">
        <div>
          <h1>Wholesale Applications</h1>
          <p className="whapp-muted">
            Approving creates a Shopify customer tagged WHSL (+EU by region). Every decision here also
            updates the Slack message and the Wholesale Program sheet.
          </p>
        </div>
        <Link className="whapp-config-link" to="/wholesale/discounts">Discount config →</Link>
      </div>

      {error && (
        <div className="whapp-error">
          {error} <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {loading ? (
        <p className="whapp-muted">Loading…</p>
      ) : (
        <>
          <h2 className="whapp-section-title">Pending ({pending.length})</h2>
          {pending.length === 0 ? (
            <p className="whapp-muted">Nothing waiting on a decision.</p>
          ) : (
            <div className="whapp-table-wrap">
              <table className="whapp-table">
                <thead>
                  <tr><th>Owner / Manager</th><th>Email</th><th>Region</th><th>Submitted</th><th>Status</th></tr>
                </thead>
                <tbody>{pending.map(a => <Row key={a.id} a={a} />)}</tbody>
              </table>
            </div>
          )}

          <h2 className="whapp-section-title">Decided ({decided.length})</h2>
          {decided.length === 0 ? (
            <p className="whapp-muted">No decisions yet.</p>
          ) : (
            <div className="whapp-table-wrap">
              <table className="whapp-table">
                <thead>
                  <tr><th>Owner / Manager</th><th>Email</th><th>Region</th><th>Submitted</th><th>Status</th></tr>
                </thead>
                <tbody>{decided.map(a => <Row key={a.id} a={a} />)}</tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
